import ReactMarkdown from "react-markdown";

import { ResponsiveBreakpoint } from "@utils/StyleConstants";
import { SocialHead } from "./all-pages/SocialHead";
import { LoadRoobert } from "@lux/atoms/components/LoadRoobert";
import { SolanaNav } from "./nav/SolanaNav";
import { Footer } from "./Footer";

export const StaticPage = ({
  title,
  path,
  header,
  markdown,
  hideNav,
}: {
  title: string;
  path: string;
  header: React.ReactNode;
  markdown: string;
  hideNav?: boolean;
}) => {
  return (
    <div className="page flex-column font-roobert">
      <LoadRoobert />
      {!hideNav && <SolanaNav />}
      <SocialHead subtitle={title} canonicalPath={path} />
      <div className="header">{header}</div>
      <StaticBody markdown={markdown} />
      <div className="flex-1" />
      {!hideNav && <Footer />}

      <style jsx>{`
        :global(body) {
          --max-width: 750px;
          --nav-height: 4rem;
          --nav-vertical-padding: 1.25rem;
          --side-padding: 2rem;

          @media (max-width: ${ResponsiveBreakpoint.small}) {
            --nav-height: 3.5rem;
            --nav-vertical-padding: 1rem;
            --side-padding: 1.5rem;
          }
        }

        .page {
          min-height: 100vh;
        }

        .header {
          width: 100%;
          max-width: var(--max-width);
          margin: auto;

          &:not(:first-child) {
            padding: 8rem var(--side-padding) 0;

            @media (max-width: ${ResponsiveBreakpoint.small}) {
              padding: 6rem var(--side-padding) 0;
            }

            @media (max-width: ${ResponsiveBreakpoint.tiny}) {
              padding: 4rem var(--side-padding) 0;
            }
          }

          &:first-child {
            padding: 4rem var(--side-padding) 0;

            @media (max-width: ${ResponsiveBreakpoint.tiny}) {
              padding: var(--side-padding);
              padding-bottom: 0;
            }
          }
        }
      `}</style>
    </div>
  );
};

export const StaticHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => {
  return (
    <div>
      <h1>{title}</h1>
      {subtitle && <div className="subtitle text-tertiary">{subtitle}</div>}
      <hr />

      <style jsx>{`
        h1 {
          font-size: 3rem;
        }

        hr {
          margin: 3rem 0;
        }

        @media (max-width: ${ResponsiveBreakpoint.small}) {
          h1 {
            font-size: 2.5rem;
          }

          hr {
            margin: 2rem 0;
          }
        }

        @media (max-width: ${ResponsiveBreakpoint.tiny}) {
          h1 {
            font-size: 2rem;
            margin-bottom: 0.75rem;
          }

          hr {
            margin: 1.5rem 0;
          }
        }
      `}</style>
    </div>
  );
};

export const StaticBody = ({ markdown }: { markdown: string }) => {
  return (
    <div className="static-wrapper zm-container pb-5">
      <div className="content">
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>

      <style jsx global>{`
        .static-wrapper {
          position: relative;
          display: block;
          max-width: var(--max-width);

          h2 {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            margin-top: 2rem;

            @media (max-width: ${ResponsiveBreakpoint.tiny}) {
              font-size: 1.25rem;
              line-height: 1.3;
              margin-top: 1.75rem;
            }
          }

          span.line {
            display: block;
          }

          .content {
            padding: 0 var(--side-padding);
          }
        }
      `}</style>
    </div>
  );
};
